import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFile,
  faDownload,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Add_Bulk_Upload, My_Store } from '../../../Api';
import { STORAGE_CONSTANTS } from '../../../shared/Constants/Constants';
import BtnLoader from './BtnLoader';
import {
  triggerSampleTemplateDownload,
  startJobPolling,
  resolveMediaUrl,
  getBulkUploadHistory,
  calculateJobProgress,
} from '../../../Api/apis/bulkUploadApi';
import {
  BulkUploadJobStatusResponse,
  BulkUploadHistoryItem,
} from '../../../types/bulkUpload';

interface BulkUploadFormProps {
  styles: any;
  t: (key: string) => string;
}

interface BulkErrorDetails {
  message: string;
  errorFilePath?: string | null;
  totalFaildProduct?: number | null;
  totalUploadProduct?: number | null;
  createdRecord?: number | null;
  error?: string | null;
}

const ProductBulkUpload: React.FC<BulkUploadFormProps> = ({ styles, t }) => {
  const navigate = useNavigate();

  // State management
  const [file, setFile] = useState<File | null>(null);
  const [zip, setZip] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [zipFileName, setZipFileName] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [bulkErrorDetails, setBulkErrorDetails] = useState<BulkErrorDetails | null>(null);

  // Status API live tracking state
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [activeJobStatus, setActiveJobStatus] = useState<BulkUploadJobStatusResponse | null>(null);
  const [progressPercent, setProgressPercent] = useState<number>(0);

  // History API state
  const [historyList, setHistoryList] = useState<BulkUploadHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState<number>(0);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyPage, setHistoryPage] = useState<number>(1);
  const historyLimit = 5;

  // Polling ref to cancel on unmount
  const cancelPollingRef = useRef<(() => void) | null>(null);

  // User & Store ID resolution
  const [storeIdState, setStoreIdState] = useState<string>(() => {
    return (
      localStorage.getItem(STORAGE_CONSTANTS.STORE_ID) ||
      localStorage.getItem(STORAGE_CONSTANTS.SHOP_ID) ||
      localStorage.getItem('storeId') ||
      localStorage.getItem('shop_id') ||
      localStorage.getItem('store_id') ||
      ''
    );
  });

  const userToken = localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN);

  const resolveStoreId = useCallback(async (): Promise<string | null> => {
    const localStoreId =
      storeIdState ||
      localStorage.getItem(STORAGE_CONSTANTS.STORE_ID) ||
      localStorage.getItem(STORAGE_CONSTANTS.SHOP_ID) ||
      localStorage.getItem('storeId') ||
      localStorage.getItem('shop_id') ||
      localStorage.getItem('store_id');

    if (localStoreId) {
      if (!storeIdState) setStoreIdState(localStoreId);
      return localStoreId;
    }

    const token = userToken || localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN);
    if (token) {
      try {
        const res = await My_Store(token);
        const fetchedId = res?.data?.my_stores?.[0]?.id;
        if (fetchedId) {
          const strId = String(fetchedId);
          localStorage.setItem(STORAGE_CONSTANTS.STORE_ID, strId);
          setStoreIdState(strId);
          return strId;
        }
      } catch (err) {
        console.warn('Failed to resolve store ID via My_Store:', err);
      }
    }

    return null;
  }, [storeIdState, userToken]);

  // Fetch real history from API
  const fetchHistory = useCallback(
    async (page: number = 1): Promise<void> => {
      try {
        setHistoryLoading(true);
        const res = await getBulkUploadHistory(
          { page, limit: historyLimit, upload_type: 'store_product' },
          userToken || undefined
        );
        setHistoryList(res.results || []);
        setHistoryTotal(res.count || 0);
        setHistoryPage(res.page || page);
      } catch (err) {
        console.error('Error fetching bulk history', err);
      } finally {
        setHistoryLoading(false);
      }
    },
    [userToken]
  );

  useEffect(() => {
    resolveStoreId();
    fetchHistory(1);
    return () => {
      if (cancelPollingRef.current) {
        cancelPollingRef.current();
      }
    };
  }, [fetchHistory, resolveStoreId]);

  // File type detection
  const getFileExtension = (filename: string): void => {
    const parts = filename.split('.');
    const ext = parts.pop()?.toLowerCase();
    if (ext === 'xls' || ext === 'xlsx') {
      setFileType('excel');
    } else if (ext === 'csv') {
      setFileType('csv');
    }
  };

  // File upload handlers
  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      getFileExtension(selectedFile.name);
      toast.info(`Selected file: ${selectedFile.name}`);
    }
    // Reset target value so selecting the same file again triggers onChange
    e.target.value = '';
  };

  const handleBulkZipChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setZip(selectedFile);
      setZipFileName(selectedFile.name);
      toast.info(`Selected zip: ${selectedFile.name}`);
    }
    e.target.value = '';
  };

  // Reset form function
  const resetForm = (): void => {
    if (cancelPollingRef.current) {
      cancelPollingRef.current();
      cancelPollingRef.current = null;
    }
    setFile(null);
    setZip(null);
    setFileType('');
    setFileName('');
    setZipFileName('');
    setLoading(false);
    setIsPolling(false);
    setActiveJobId(null);
    setActiveJobStatus(null);
    setProgressPercent(0);
  };

  // Download template using the real API endpoint with auth
  const handleTemplateDownload = async (e: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    e.preventDefault();
    try {
      const type = fileType === 'excel' ? 'excel' : 'csv';
      const downloadedFileName = await triggerSampleTemplateDownload(type, userToken || undefined);
      toast.success(`Downloaded template: ${downloadedFileName}`);
    } catch (err: any) {
      console.error('Error downloading sample template', err);
      toast.error('Failed to download template. Please check your credentials or network.');
    }
  };

  // Main form submission handler
  const handleFormSubmit = async (e?: React.FormEvent | React.MouseEvent): Promise<void> => {
    if (e) {
      e.preventDefault();
      if ('stopPropagation' in e) {
        e.stopPropagation();
      }
    }

    console.log('[BulkUpload] Submit initiated', {
      file: file ? file.name : null,
      zip: zip ? zip.name : null,
      fileType,
    });

    if (!file) {
      toast.error('Please select a CSV or Excel file to upload');
      return;
    }

    const activeToken = userToken || localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN);
    if (!activeToken) {
      toast.error('Authentication token not found. Please log in first.');
      return;
    }

    let targetStoreId = await resolveStoreId();
    if (!targetStoreId) {
      toast.error('Store ID not found. Please verify your store profile.');
      return;
    }

    try {
      setLoading(true);
      setIsPolling(false);
      setBulkErrorDetails(null);
      setActiveJobStatus(null);
      setProgressPercent(0);

      console.log('[BulkUpload] Calling Add_Bulk_Upload with:', {
        storeId: targetStoreId,
        fileName: file.name,
        fileSize: file.size,
      });

      // Call the real bulk upload queue API endpoint
      const response = await Add_Bulk_Upload(
        activeToken,
        targetStoreId,
        file,
        zip,
        fileType
      );

      console.log('[BulkUpload] Add_Bulk_Upload response:', response);

      const jobId = response?.data?.job_id;

      // When the job is queued (status 200 or 201)
      if (
        (response?.status === 200 ||
          response?.status === 201 ||
          response?.data?.status === 200) &&
        jobId
      ) {
        setActiveJobId(jobId);
        setIsPolling(true);
        toast.info(response.data?.message || `Upload queued (Job #${jobId}). Processing in background...`);

        if (cancelPollingRef.current) {
          cancelPollingRef.current();
        }

        // Poll the real job status dynamically from the backend until completed or failed
        cancelPollingRef.current = startJobPolling(jobId, {
          customToken: activeToken || undefined,
          onProgress: (status: BulkUploadJobStatusResponse, progress: number) => {
            setActiveJobStatus(status);
            setProgressPercent(progress);
          },
          onSuccess: (finalStatus: BulkUploadJobStatusResponse) => {
            setLoading(false);
            setIsPolling(false);
            setActiveJobStatus(finalStatus);
            setProgressPercent(100);

            // Refresh real history list with the newly completed job
            fetchHistory(1);

            if (finalStatus.failed_rows > 0) {
              setBulkErrorDetails({
                message: finalStatus.error_summary || 'Bulk upload completed with errors.',
                errorFilePath: resolveMediaUrl(finalStatus.error_log_url),
                totalFaildProduct: finalStatus.failed_rows,
                totalUploadProduct: finalStatus.total_rows,
                createdRecord: finalStatus.created_rows,
                error: finalStatus.error_summary,
              });

              toast.warn(
                `${finalStatus.created_rows} products created, ${finalStatus.failed_rows} failed.`
              );
            } else {
              toast.success(`Bulk upload successful! ${finalStatus.created_rows} products added.`);
              navigate('/products');
              resetForm();
            }

            if (finalStatus.image_fallback_count > 0) {
              toast.info(
                `${finalStatus.image_fallback_count} product(s) used platform default placeholder images.`
              );
            }
          },
          onFailure: (finalStatus: BulkUploadJobStatusResponse) => {
            setLoading(false);
            setIsPolling(false);
            setActiveJobStatus(finalStatus);

            fetchHistory(1);

            setBulkErrorDetails({
              message: finalStatus.error_summary || 'Bulk upload failed with an unexpected error.',
              errorFilePath: resolveMediaUrl(finalStatus.error_log_url),
              totalFaildProduct: finalStatus.failed_rows || 0,
              totalUploadProduct: finalStatus.total_rows || 0,
              createdRecord: finalStatus.created_rows || 0,
              error: finalStatus.error_summary,
            });
            toast.error(finalStatus.error_summary || 'Bulk upload failed.');
          },
          onTimeout: () => {
            setLoading(false);
            setIsPolling(false);
            toast.warn('Processing is taking longer than 15 minutes. It will finish in the background.');
          },
          onError: (err: any) => {
            console.warn('Status poll glitch, retrying...', err?.message || err);
          },
        });
      } else {
        setLoading(false);
        setIsPolling(false);
        const errMsg = response?.data?.message || response?.data?.error || 'Bulk upload failed to queue.';
        toast.error(errMsg);
        setBulkErrorDetails({
          message: errMsg,
          errorFilePath: null,
          totalFaildProduct: null,
          totalUploadProduct: null,
          createdRecord: null,
          error: response?.data?.error || null,
        });
      }
    } catch (error: any) {
      setLoading(false);
      setIsPolling(false);
      console.error('[BulkUpload] Error occurred:', error.response?.data || error);

      const errData = error?.response?.data;
      const errorMsg =
        errData?.message ||
        errData?.error ||
        errData?.detail ||
        (typeof errData === 'string' ? errData : null) ||
        error?.message ||
        'Bulk upload failed with an unexpected error.';

      toast.error(errorMsg);

      if (errData) {
        setBulkErrorDetails({
          message: errorMsg,
          errorFilePath: resolveMediaUrl(errData.error_log_url || errData.error_file_path || null),
          totalFaildProduct: errData.failed_rows ?? errData.total_faild_product ?? null,
          totalUploadProduct: errData.total_rows ?? errData.total_upload_product ?? null,
          createdRecord: errData.created_rows ?? errData.created ?? null,
          error: errData.error || errData.error_summary || null,
        });
      } else {
        setBulkErrorDetails({
          message: errorMsg,
          errorFilePath: null,
          totalFaildProduct: null,
          totalUploadProduct: null,
          createdRecord: null,
          error: null,
        });
      }
    }
  };

  return (
    <Row
      className="text-center mt-5"
      style={{ maxWidth: 600, margin: 'auto' }}
    >
      <Col>
        <div
          className="bulk-upload-form"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleFormSubmit(e as any);
            }
          }}
        >
          {/* CSV/Excel File Upload */}
          <div>
            <label
              htmlFor="csvFile"
              className="file-upload-label"
              style={{ ...styles.uploadTxt, cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faFile as any} className="mx-1" />
              {t('product.upload')}
            </label>
            <input
              type="file"
              id="csvFile"
              accept=".csv, .xls, .xlsx"
              onChange={handleBulkFileChange}
              style={{ display: 'none' }}
            />
            <p style={styles.uploadedFile}>{fileName}</p>
          </div>

          {/* Zip File Upload */}
          <div>
            <label
              htmlFor="zipFile"
              className="file-upload-label"
              style={{ ...styles.uploadTxt, cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faFile as any} className="mx-1" />
              {'Zip File'}
            </label>
            <input
              type="file"
              id="zipFile"
              accept=".zip"
              onChange={handleBulkZipChange}
              style={{ display: 'none' }}
            />
            <p style={styles.uploadedFile}>{zipFileName}</p>
          </div>

          {/* Real-time Status API Progress (Shown when a job is active/processing) */}
          {(isLoading || isPolling || activeJobStatus) && (
            <div style={{ marginTop: 15, marginBottom: 15, textAlign: 'left' }}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                  {activeJobStatus?.status === 'processing'
                    ? `Processing rows (${progressPercent}%)...`
                    : activeJobStatus?.status === 'pending'
                    ? 'Queued in background...'
                    : activeJobStatus?.status === 'completed'
                    ? 'Ingestion completed'
                    : activeJobStatus?.status === 'failed'
                    ? 'Job failed'
                    : 'Uploading spreadsheet...'}
                </span>
                {activeJobId && (
                  <span style={{ fontSize: 12, color: '#666' }}>Job #{activeJobId}</span>
                )}
              </div>
              <ProgressBar
                now={progressPercent}
                label={progressPercent > 0 ? `${progressPercent}%` : undefined}
                variant={
                  activeJobStatus?.status === 'failed'
                    ? 'danger'
                    : (activeJobStatus?.failed_rows || 0) > 0
                    ? 'warning'
                    : 'primary'
                }
                style={{ height: 8, borderRadius: 4 }}
              />
            </div>
          )}

          {/* Error Details Display */}
          {bulkErrorDetails?.message && (
            <div style={{ ...styles.uploadedFile, color: 'red' }}>
              <p>
                <b>{bulkErrorDetails.message}</b>
                <br />
                Total Records: {bulkErrorDetails.totalUploadProduct || 0},
                Success: {bulkErrorDetails.createdRecord || 0},
                Failure: {bulkErrorDetails.totalFaildProduct || 0}
                <br />
                {bulkErrorDetails?.error && (
                  <span style={{ color: 'red' }}>
                    Error: {bulkErrorDetails.error}
                  </span>
                )}
                <br />
                {bulkErrorDetails?.errorFilePath && (
                  <a
                    href={bulkErrorDetails.errorFilePath}
                    download="error_log.csv"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    Click here to download file with error records
                  </a>
                )}
              </p>
            </div>
          )}

          {/* Template Download Section */}
          <h3 style={styles.labelText}>{t('product.bulkInfo')}</h3>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faFile as any} style={styles.templateIcon} />
              <h3 style={styles.templateText} className="mb-0">
                Template 1
              </h3>
            </div>
            <a
              href="#download"
              onClick={handleTemplateDownload}
              style={{ cursor: 'pointer' }}
            >
              {t('product.download')}
            </a>
          </div>

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleFormSubmit}
            disabled={isLoading || isPolling}
            className="btnBlue submit"
          >
            {isLoading || isPolling ? <BtnLoader /> : t('commonFormLabels.submit')}
          </button>
        </div>

        {/* Upload History Section (Dynamic real data from History API) */}
        <div style={{ marginTop: 40, textAlign: 'left' }}>
          <div className="d-flex align-items-center justify-content-between">
            <h3 style={{ ...styles.labelText, marginTop: 0, borderTop: 'none', marginBottom: 12, flex: 1 }}>
              Upload History
            </h3>
            <button
              type="button"
              onClick={() => fetchHistory(historyPage)}
              disabled={historyLoading}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary-color, #075CF6)',
                fontSize: 13,
                cursor: 'pointer',
                padding: 0,
                marginBottom: 12,
              }}
            >
              <FontAwesomeIcon icon={faSync as any} spin={historyLoading} className="me-1" />
              Refresh
            </button>
          </div>

          {historyLoading && historyList.length === 0 ? (
            <p style={{ ...styles.uploadedFile, textAlign: 'center', color: '#888' }}>
              Loading history...
            </p>
          ) : historyList.length === 0 ? (
            <p style={{ ...styles.uploadedFile, textAlign: 'center', color: '#888' }}>
              No previous bulk upload jobs found.
            </p>
          ) : (
            <>
              <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                  <thead style={{ backgroundColor: '#F8FAFC' }}>
                    <tr>
                      <th style={{ padding: '8px 10px' }}>Job #</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                      <th style={{ padding: '8px 10px' }}>Created / Total</th>
                      <th style={{ padding: '8px 10px' }}>Failed</th>
                      <th style={{ padding: '8px 10px' }}>Date</th>
                      <th style={{ padding: '8px 10px' }}>Log</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyList.map((item) => {
                      const errUrl = resolveMediaUrl(item.error_log_url);
                      return (
                        <tr key={item.job_id} style={{ verticalAlign: 'middle' }}>
                          <td style={{ padding: '8px 10px' }}><strong>#{item.job_id}</strong></td>
                          <td style={{ padding: '8px 10px' }}>
                            <span
                              style={{
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                backgroundColor:
                                  item.status === 'completed'
                                    ? item.failed_rows > 0
                                      ? '#FEF3C7'
                                      : '#DCFCE7'
                                    : item.status === 'failed'
                                    ? '#FEE2E2'
                                    : '#EFF6FF',
                                color:
                                  item.status === 'completed'
                                    ? item.failed_rows > 0
                                      ? '#92400E'
                                      : '#15803D'
                                    : item.status === 'failed'
                                    ? '#B91C1C'
                                    : '#1D4ED8',
                              }}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px' }}>{item.created_rows} / {item.total_rows}</td>
                          <td style={{ padding: '8px 10px' }}>
                            {item.failed_rows > 0 ? (
                              <span style={{ color: '#DC2626', fontWeight: 600 }}>
                                {item.failed_rows}
                              </span>
                            ) : (
                              '0'
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#666' }}>
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            {errUrl ? (
                              <a
                                href={errUrl}
                                download={`error_log_job_${item.job_id}.csv`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#DC2626', textDecoration: 'underline', fontSize: 12 }}
                              >
                                <FontAwesomeIcon icon={faDownload as any} className="me-1" />
                                CSV
                              </a>
                            ) : (
                              <span style={{ color: '#AAA' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {historyTotal > historyLimit && (
                <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: 12 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                    disabled={historyPage <= 1 || historyLoading}
                    onClick={() => fetchHistory(historyPage - 1)}
                  >
                    Prev
                  </button>
                  <span style={{ color: '#666' }}>
                    Page {historyPage} of {Math.ceil(historyTotal / historyLimit)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                    disabled={historyPage >= Math.ceil(historyTotal / historyLimit) || historyLoading}
                    onClick={() => fetchHistory(historyPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default ProductBulkUpload;